import React, { memo, useState, useRef, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { useLazyImage } from '../../hooks/usePerformance';
import { getOptimizedImageUrl, getOptimalSize, generateSrcSet, generateSizes } from '../../utils/imageUtils';

const OptimizedImage = memo(({ 
  src, 
  alt = '', 
  width, 
  height, 
  className = '',
  placeholder = '/placeholder.jpg',
  lazy = true,
  quality = 'auto',
  format = 'auto',
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef();

  // Используем хук для ленивой загрузки
  const { imgRef: lazyRef, isLoaded: lazyLoaded, isInView, error } = useLazyImage(
    lazy ? src : null,
    { threshold: 0.1, rootMargin: '50px' }
  );

  // Объединяем refs
  useEffect(() => {
    if (imgRef.current && lazyRef.current) {
      imgRef.current = lazyRef.current;
    }
  }, [lazyRef]);

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(false);
  };

  // Генерируем оптимизированный URL
  const getOptimizedSrc = (originalSrc) => {
    if (!originalSrc) return placeholder;
    
    // Используем утилиту для оптимизации изображений
    const optimalSize = width ? parseInt(width) : getOptimalSize('detail', 'cover');
    return getOptimizedImageUrl(originalSrc, optimalSize);
  };

  const optimizedSrc = getOptimizedSrc(src);
  const shouldLoad = lazy ? isInView : true;

  return (
    <div 
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      style={{ width, height }}
    >
      {/* Placeholder */}
      {!isLoaded && !hasError && (
        <div 
          className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center"
          style={{ width, height }}
        >
          <div className="text-gray-400 text-sm">Загрузка...</div>
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div 
          className="absolute inset-0 bg-gray-100 flex items-center justify-center"
          style={{ width, height }}
        >
          <div className="text-center text-gray-500">
            <div className="text-2xl mb-2">🖼️</div>
            <div className="text-sm">Ошибка загрузки</div>
          </div>
        </div>
      )}

      {/* Actual image */}
      {shouldLoad && (
        <img
          src={optimizedSrc}
          srcSet={src ? generateSrcSet(src, [320, 640, 1280, 1920]) : undefined}
          sizes={generateSizes('detail')}
          alt={alt}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          style={{ 
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          loading={lazy ? 'lazy' : 'eager'}
          decoding="async"
          {...(props.onError ? {} : {})} // Не перезаписываем onError если передан в props
          {...props}
        />
      )}
    </div>
  );
});

OptimizedImage.displayName = 'OptimizedImage';

// Компонент для галереи изображений с оптимизацией
export const OptimizedImageGallery = memo(({ 
  images, 
  columns = 3,
  gap = 4,
  itemHeight = 200,
  className = ''
}) => {
  const [selectedImage, setSelectedImage] = useState(null);

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: `${gap * 0.25}rem`
  };

  return (
    <div className={className}>
      <div style={gridStyle}>
        {images.map((image, index) => (
          <div
            key={image.id || index}
            className="cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => setSelectedImage(image)}
          >
            <OptimizedImage
              src={image.src}
              alt={image.alt || `Изображение ${index + 1}`}
              width="100%"
              height={itemHeight}
              lazy={true}
              className="rounded-lg"
            />
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              ✕
            </button>
            <OptimizedImage
              src={selectedImage.src}
              alt={selectedImage.alt}
              width="100%"
              height="auto"
              lazy={false}
              className="rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
});

OptimizedImageGallery.displayName = 'OptimizedImageGallery';

// Функция для генерации инициалов из имени
const getInitials = (firstName, lastName, username, alt) => {
  if (firstName || lastName) {
    const first = (firstName || '').charAt(0).toUpperCase();
    const last = (lastName || '').charAt(0).toUpperCase();
    return first + last;
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  if (alt) {
    // Пытаемся извлечь инициалы из alt (может быть "Имя Фамилия" или "Имя Фамилия Avatar")
    const parts = alt.split(' ').filter(p => p && p !== 'Avatar');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return parts[0]?.charAt(0).toUpperCase() || '?';
  }
  return '?';
};

// Компонент для аватаров с оптимизацией и инициалами
export const OptimizedAvatar = memo(({ 
  src, 
  alt, 
  firstName,
  lastName,
  username,
  size = 40,
  fallback = null,
  className = '',
  clickable = true, // Позволяет отключить клик для просмотра
  onImageClick = null // Кастомный обработчик клика (если нужно другое поведение)
}) => {
  const [hasError, setHasError] = useState(false);
  const [showFullSize, setShowFullSize] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const imgRef = useRef(null);
  
  // Сбрасываем состояние ошибки при изменении src
  useEffect(() => {
    if (src) {
      setHasError(false);
    }
  }, [src]);

  const handleError = () => {
    setHasError(true);
  };

  // Нормализуем URL
  const normalizedSrc = useMemo(() => {
    // Если src это объект (например, sql.NullString из Go), извлекаем строку
    if (src && typeof src === 'object') {
      src = src.String || src.string || null;
    }
    
    if (!src || typeof src !== 'string') {
      return null;
    }
    
    let url = src.trim();
    if (!url) return url;
    
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    const apiBase = process.env.REACT_APP_API_URL || 'https://api.travmap.org';
    const baseUrl = apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
    
    if (url.startsWith('/files/')) {
      return `${baseUrl}${url}`;
    }
    
    if (url.startsWith('/')) {
      return `${baseUrl}${url}`;
    }
    
    if (url.startsWith('avatars/') || url.startsWith('videos/')) {
      return `${baseUrl}/files/${url}`;
    }
    
    return url;
  }, [src]);
  
  // Оптимизируем URL аватара в зависимости от размера
  const optimizedSrc = useMemo(() => {
    if (!normalizedSrc || hasError) {
      return normalizedSrc;
    }
    
    // Определяем оптимальный размер напрямую (избегаем проблем с инициализацией)
    let optimalSize = 40; // AVATAR_THUMBNAIL по умолчанию
    if (size > 128) {
      optimalSize = 256; // AVATAR_LARGE
    } else if (size > 40) {
      optimalSize = 128; // AVATAR_MEDIUM
    }
    
    // Оптимизируем URL напрямую (избегаем проблем с импортами)
    if (!normalizedSrc || typeof normalizedSrc !== 'string') {
      return normalizedSrc;
    }
    
    let url = normalizedSrc.trim();
    if (!url) return url;
    
    const quality = 85;
    
    // Если URL уже полный, добавляем query параметры
    if (url.startsWith('http://') || url.startsWith('https://')) {
      if (optimalSize > 0) {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}w=${optimalSize}&q=${quality}`;
      }
      return url;
    }
    
    // Для относительных путей добавляем параметры
    if (optimalSize > 0) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}w=${optimalSize}&q=${quality}`;
    }
    
    return url;
  }, [normalizedSrc, hasError, size]);

  // Загружаем изображение для определения его размеров
  useEffect(() => {
    if (normalizedSrc && !hasError) {
      const img = new Image();
      img.onload = () => {
        setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.src = normalizedSrc;
    }
  }, [normalizedSrc, hasError]);

  // Обработка закрытия по ESC
  useEffect(() => {
    if (!showFullSize) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowFullSize(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    // Блокируем скролл фона
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [showFullSize]);

  // Определяем оптимальный размер для просмотра в зависимости от качества фото
  const getOptimalSize = () => {
    if (!imageSize.width || !imageSize.height) {
      // Если размеры еще не загружены, используем дефолтные значения
      return { maxWidth: '90vw', maxHeight: '90vh' };
    }

    const aspectRatio = imageSize.width / imageSize.height;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Определяем максимальный размер в зависимости от качества изображения
    let maxWidth = Math.min(imageSize.width, viewportWidth * 0.9);
    let maxHeight = Math.min(imageSize.height, viewportHeight * 0.9);

    // Если изображение высокого разрешения (больше 1920px), ограничиваем размер
    if (imageSize.width > 1920) {
      maxWidth = Math.min(1920, viewportWidth * 0.9);
      maxHeight = maxWidth / aspectRatio;
    }

    // Если изображение очень маленькое, показываем его в оригинальном размере
    if (imageSize.width < 400) {
      maxWidth = imageSize.width;
      maxHeight = imageSize.height;
    }

    // Корректируем размеры с учетом aspect ratio
    if (maxHeight > viewportHeight * 0.9) {
      maxHeight = viewportHeight * 0.9;
      maxWidth = maxHeight * aspectRatio;
    }

    return { maxWidth: `${maxWidth}px`, maxHeight: `${maxHeight}px` };
  };

  const handleAvatarClick = (e) => {
    if (!clickable || hasError || !normalizedSrc || normalizedSrc === '/default-avatar.png') {
      return;
    }

    if (onImageClick) {
      onImageClick(e);
    } else {
      setShowFullSize(true);
    }
  };

  // Определяем цвет фона на основе инициалов (для консистентности)
  const getBgColor = (initials) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-slate-500', 'bg-pink-500',
      'bg-indigo-500', 'bg-yellow-500', 'bg-red-500', 'bg-teal-500',
      'bg-orange-500', 'bg-cyan-500'
    ];
    const index = (initials.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  if (hasError || !normalizedSrc || normalizedSrc === '/default-avatar.png') {
    const initials = fallback || getInitials(firstName, lastName, username, alt);
    const bgColor = getBgColor(initials);
    // Увеличиваем размер шрифта для лучшей читаемости
    let fontSize = 'text-base';
    if (size <= 32) {
      fontSize = 'text-sm';
    } else if (size <= 48) {
      fontSize = 'text-lg';
    } else if (size <= 64) {
      fontSize = 'text-xl';
    } else if (size <= 96) {
      fontSize = 'text-2xl';
    } else {
      fontSize = 'text-3xl';
    }
    
    return (
      <div 
        className={`${bgColor} text-white rounded-full flex items-center justify-center font-semibold ${fontSize} ${className}`}
        style={{ width: size, height: size, minWidth: size, minHeight: size }}
      >
        {initials}
      </div>
    );
  }

  const optimalSize = getOptimalSize();

  return (
    <>
      <div 
        className={`relative ${clickable && normalizedSrc && !hasError ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
        style={{ width: size, height: size }}
        onClick={handleAvatarClick}
      >
        <img
          ref={imgRef}
          key={optimizedSrc} // Добавляем key для принудительного обновления при изменении src
          src={optimizedSrc}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={(e) => {
            // console.error('Avatar image load error:', {
            //   originalSrc: src,
            //   normalizedSrc: normalizedSrc,
            //   error: e,
            //   target: e.target
            // });
            handleError();
          }}
          className={`rounded-full object-cover ${className}`}
          style={{ width: size, height: size }}
        />
      </div>

      {/* Модальное окно для просмотра увеличенного аватара */}
      {showFullSize && normalizedSrc && !hasError && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75 p-4"
          onClick={() => setShowFullSize(false)}
        >
          <div 
            className="relative max-w-full max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Кнопка закрытия */}
            <button
              onClick={() => setShowFullSize(false)}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white hover:text-gray-300 z-10 bg-black bg-opacity-50 rounded-full p-2 transition-colors"
              aria-label="Закрыть"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>

            {/* Изображение */}
            <img
              src={optimizedSrc || normalizedSrc}
              srcSet={normalizedSrc ? generateSrcSet(normalizedSrc, [128, 256, 512]) : undefined}
              sizes="(max-width: 768px) 90vw, 512px"
              alt={alt || 'Аватар'}
              className="rounded-lg shadow-2xl object-contain"
              decoding="async"
              style={{
                maxWidth: optimalSize.maxWidth,
                maxHeight: optimalSize.maxHeight,
                width: 'auto',
                height: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
});

OptimizedAvatar.displayName = 'OptimizedAvatar';

export default OptimizedImage;
